import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { Brand, BrandId, brands } from '../theme/brands';
interface BrandContextValue {
  brand: Brand;
  /** Swaps the operator name into shared copy so one content set serves every brand. */
  text: (value: string) => string;
  carePathImage: (slug: string, fallback: string) => string;
  treatmentImage: (slug: string, fallback: string) => string;
}
const BrandContext = createContext<BrandContextValue | null>(null);
export function BrandProvider({
  brandId = 'vesta',
  children
}: {
  brandId?: BrandId;
  children: React.ReactNode;
}) {
  const brand = brands[brandId] ?? brands.vesta;
  useEffect(() => {
    document.documentElement.setAttribute('data-brand', brand.id);
  }, [brand.id]);

  // The tab icon is drawn from the brand mark itself, so a new brand needs no asset upload.
  useEffect(() => {
    const href = `data:image/svg+xml,${encodeURIComponent(brand.favicon)}`;
    const apply = (rel: string) => {
      let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('type', 'image/svg+xml');
      link.setAttribute('href', href);
    };
    apply('icon');
    apply('apple-touch-icon');
  }, [brand.favicon]);
  const text = useCallback((value: string) => value.replace(/\{brand\}/g, brand.name), [brand.name]);
  const carePathImage = useCallback((slug: string, fallback: string) => brand.images.carePaths[slug] ?? fallback, [brand]);
  const treatmentImage = useCallback((slug: string, fallback: string) => brand.images.treatments[slug] ?? fallback, [brand]);
  const value = useMemo(() => ({
    brand,
    text,
    carePathImage,
    treatmentImage
  }), [brand, text, carePathImage, treatmentImage]);
  return <BrandContext.Provider value={value} data-id="element-383" data-mp="6qs9t9zudx0h">{children}</BrandContext.Provider>;
}
export function useBrand(): BrandContextValue {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used inside a BrandProvider');
  }
  return context;
}